/**
 * H2 — per-asset journey over GET /api/v2/assets/{assetId}/trace (H1).
 * One merged, newest-first stream: workflow transitions, shipment scans,
 * and legacy status rows. All display data (state names/icons/colors,
 * location hops, order refs) is resolved server-side against the PINNED
 * workflow definition — the UI never guesses.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface TraceState {
  code: string;
  name: string | null;
  uiIcon: string | null;
  uiBadgeColor: string | null;
}

export interface TraceShipment {
  id: number;
  sourceLocation: string | null;
  destinationLocation: string | null;
  destinationClient: string | null;
  scanStage: string | null;
  orderCode: string | null;
  orderExternalRef: string | null;
}

export type TraceKind = 'TRANSITION' | 'SHIPMENT_DISPATCH' | 'SHIPMENT_RECEIVE' | 'LEGACY_STATUS';

export interface AssetTraceEvent {
  occurredAt: string;
  kind: TraceKind;
  transitionCode: string | null;
  transitionName: string | null;
  fromState: TraceState | null;
  toState: TraceState | null;
  shipment: TraceShipment | null;
  formData: Record<string, unknown> | null;
  legacyStatus: number | null;
  description: string | null;
  userId: number | null;
  userName: string | null;
}

export interface AssetTraceResult {
  assetId: string;
  total: number;
  page: number;
  pageSize: number;
  events: AssetTraceEvent[];
}

export function useAssetTrace(assetId: string | null, pageSize = 50, enabled = true) {
  return useQuery({
    queryKey: ['asset-trace', assetId, pageSize],
    enabled: enabled && !!assetId,
    queryFn: async () => {
      const res = await api.get<AssetTraceResult>(
        `/api/v2/assets/${encodeURIComponent(assetId!)}/trace?pageSize=${pageSize}`
      );
      return res.data;
    },
  });
}
